import React, { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, InfoIcon } from "lucide-react";
import { buttonClass } from "./Button";
import { useConsultationForm } from "../../hooks/useConsultationForm";
import type { ProjectType, ConsultationValues } from "../../hooks/useConsultationForm";
import { designs } from "../../data/designs";

// [Conversion] [Lead Gen] Two-step consultation request. Prototype: local validation only, nothing is sent.
const projectOptions: {
  value: ProjectType;
  label: string;
}[] = [{
  value: "design",
  label: "A single-storey design"
}, {
  value: "knockdown",
  label: "Knock-down/rebuild"
}, {
  value: "rear-block",
  label: "A rear-block home"
}, {
  value: "custom",
  label: "A custom home"
}, {
  value: "development",
  label: "Investment or development"
}, {
  value: "unsure",
  label: "Not sure yet"
}];
const landOptions = ["Our current home (to rebuild)", "Subdividing our block", "I own a vacant block", "Buying or under contract", "Looking for land", "Not sure"];
const timingOptions = ["Within 6 months", "6–12 months", "More than 12 months", "Just exploring"];
const budgetOptions = ["Prefer not to say", "Under $500,000", "$500,000–$750,000", "$750,000–$1,000,000", "$1,000,000–$1,500,000", "Over $1,500,000"];
const input = "mt-2 block w-full rounded-xl border bg-white px-4 py-3 text-base text-charcoal placeholder:text-slate/70 focus:outline-none focus:ring-2 focus:ring-ocean";
const fieldBorder = (error?: string) => error ? "border-alert" : "border-line focus:border-charcoal";
const label = "block text-[15px] font-semibold text-charcoal";
function readInitial(params: URLSearchParams): Partial<ConsultationValues> {
  const designSlug = params.get("design");
  const design = designs.find(item => item.slug === designSlug);
  const project = params.get("project") as ProjectType | null;
  const validProject = projectOptions.some(option => option.value === project) ? project as ProjectType : "";
  return {
    design: design ? design.name : "",
    projectType: design ? "design" : validProject,
    request: params.get("request") === "floor-plan" ? "floor-plan" : ""
  };
}
export function ConsultationForm() {
  const [params] = useSearchParams();
  const {
    step,
    values,
    errors,
    update,
    next,
    back,
    submit,
    restart
  } = useConsultationForm(readInitial(params));
  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const errorCount = Object.values(errors).filter(Boolean).length;
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);
  const onNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (!next()) requestAnimationFrame(() => errorRef.current?.focus());
  };
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!submit()) requestAnimationFrame(() => errorRef.current?.focus());
  };
  if (step === "done") {
    return <div className="rounded-[28px] border border-line bg-white p-6 md:p-10" role="status" data-id="element-435" data-mp="esi658k0qmru">
        <CheckCircle2Icon className="h-8 w-8 text-charcoal" aria-hidden="true" data-id="element-436" data-mp="sjwmt8qgebvq" />
        <h3 ref={headingRef} tabIndex={-1} className="mt-4 text-2xl font-semibold focus:outline-none" data-id="element-437" data-mp="oiumspoppvsp">
          Demo complete. Nothing has been sent.
        </h3>
        <p className="mt-3 text-[16px] leading-relaxed text-slate" data-id="element-438" data-mp="vwbe6fg9e1n">
          Your entries passed validation. On the live site, once the server confirms receipt, visitors would see: “Thanks, your
          consultation request has been received. The team will contact you to discuss your plans and confirm a suitable time.”
        </p>
        <dl className="mt-6 grid gap-3 border-t border-line pt-5 text-[15px] sm:grid-cols-2" data-id="element-439" data-mp="1l93v2zfpmfu6">
          <div data-id="element-440" data-mp="1gb22nnwe9461"><dt className="text-slate" data-id="element-441" data-mp="1gb231p0b366k">Project</dt><dd className="font-medium" data-id="element-442" data-mp="1ga0p9crwo5l5">{projectOptions.find(o => o.value === values.projectType)?.label}</dd></div>
          {values.design && <div data-id="element-443" data-mp="2gjtoi2ckq21n"><dt className="text-slate" data-id="element-444" data-mp="2gjtpo5ob8838">Design</dt><dd className="font-medium" data-id="element-445" data-mp="2gkv0qa5bsuky">{values.design}</dd></div>}
          <div data-id="element-446" data-mp="2liwm51q855zl"><dt className="text-slate" data-id="element-447" data-mp="2lj68mfc28v0j">Location</dt><dd className="font-medium" data-id="element-448" data-mp="2li3m0jlrteqf">{values.suburbUndecided ? "Still deciding" : values.suburb}</dd></div>
          <div data-id="element-449" data-mp="2qjkkk8gs8p3u"><dt className="text-slate" data-id="element-450" data-mp="2qjkh1yhkq6z3">Contact by</dt><dd className="font-medium" data-id="element-451" data-mp="2ujuozgoxf7gl">{values.contactMethod === "email" ? "Email" : "Phone"}</dd></div>
        </dl>
        <button type="button" onClick={restart} className={buttonClass("secondary", "mt-6")} data-id="element-452" data-mp="2zi5whkrr15vn">Start again</button>
      </div>;
  }
  return <div className="rounded-[28px] border border-line bg-white p-6 shadow-[0_30px_60px_-40px_rgba(43,39,39,0.4)] md:p-10" data-id="element-453" data-mp="34a64m0hz353b">
      <div className="flex gap-3 rounded-2xl bg-paper px-4 py-3 text-sm text-charcoal" data-id="element-454" data-mp="39abouvlwxpbd">
        <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-455" data-mp="3e5hbxxr1xa11" />
        <p data-id="element-456" data-mp="3i9ozhrlrwjxo">Prototype demonstration: entries are checked in your browser only. Nothing is sent and no appointment is created.</p>
      </div>

      <ol className="mt-6 grid grid-cols-2 gap-2 text-sm" aria-label="Form progress" data-id="element-457" data-mp="12k8em1drw9du">
        {["Your project", "Your details"].map((name, index) => {
        const current = step === 1 && index === 0 || step === 2 && index === 1;
        const complete = step === 2 && index === 0;
        return <li key={name} aria-current={current ? "step" : undefined} className={`border-t-2 pt-2 ${current || complete ? "border-charcoal text-charcoal" : "border-line text-slate"}`} data-id="element-458" data-mp="1qab2e5anmt6n" data-mpi={index}>
              <span className="font-semibold" data-id="element-459" data-mp="1gjzvc9voc3vj">{index + 1}.</span> {name}
            </li>;
      })}
      </ol>

      {errorCount > 0 && <div ref={errorRef} tabIndex={-1} role="alert" className="mt-6 rounded-xl border-l-4 border-alert bg-[#FBF1EE] px-4 py-3 text-[15px] text-alert focus:outline-none" data-id="element-460" data-mp="1bk3qk8f5k8ex">
          Please check {errorCount === 1 ? "1 field" : `${errorCount} fields`} below.
        </div>}

      {values.design && <p className="mt-6 text-[15px] text-slate" data-id="element-461" data-mp="2cjzkc80z4q4">
          Enquiring about <span className="font-semibold text-charcoal" data-id="element-462" data-mp="6dvmg9zilbum">{values.design}</span>
          {values.request === "floor-plan" && " (floor plan request)"}
        </p>}

      {step === 1 && <form onSubmit={onNext} noValidate className="mt-6 space-y-6" data-id="element-463" data-mp="3tpwk1g9yj8mq">
          <h3 ref={headingRef} tabIndex={-1} className="text-xl font-semibold focus:outline-none" data-id="element-464" data-mp="3etfamulbw42t">Tell us about your project</h3>
          <fieldset aria-describedby={errors.projectType ? "projectType-error" : undefined} data-id="element-465" data-mp="3jss5o2mcshgh">
            <legend className={label} data-id="element-466" data-mp="35tiqn69vvte3">What are you planning?</legend>
            <div className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-467" data-mp="3aru0hh06i3wb">
              {projectOptions.map(option => <label key={option.value} className={`flex min-h-[52px] cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-[15px] transition-colors duration-150 ease-out ${values.projectType === option.value ? "border-charcoal bg-paper" : "border-line hover:border-slate"}`} data-id="element-468" data-mp="141nih0mu670m">
                  <input type="radio" name="projectType" value={option.value} checked={values.projectType === option.value} onChange={() => update("projectType", option.value)} className="h-4 w-4 accent-charcoal" data-id="element-469" data-mp="1e5b1bkr5sczh" />
                  {option.label}
                </label>)}
            </div>
            {errors.projectType && <p id="projectType-error" className="mt-2 text-sm text-alert" data-id="element-470" data-mp="21u3x9h7s6ku3">{errors.projectType}</p>}
          </fieldset>

          <div data-id="element-471" data-mp="1wv0slxvpqgmn">
            <label htmlFor="landStatus" className={label} data-id="element-472" data-mp="1wvkchk4xbf3b">Where are you at with land?</label>
            <select id="landStatus" value={values.landStatus} onChange={e => update("landStatus", e.target.value)} aria-invalid={!!errors.landStatus} aria-describedby={errors.landStatus ? "landStatus-error" : undefined} className={`${input} ${fieldBorder(errors.landStatus)}`} data-id="element-473" data-mp="2av1vfivjfy4r">
              <option value="" data-id="element-474" data-mp="cha8x35rdb4d">Select an option</option>
              {landOptions.map(option => <option key={option} data-id="element-475" data-mp="111owrpvbouq9">{option}</option>)}
            </select>
            {errors.landStatus && <p id="landStatus-error" className="mt-2 text-sm text-alert" data-id="element-476" data-mp="15z6xwu33m5np">{errors.landStatus}</p>}
          </div>

          <div data-id="element-477" data-mp="1arroznume4nj">
            <label htmlFor="suburb" className={label} data-id="element-478" data-mp="1ar8g0wmy6qln">Build suburb or postcode</label>
            <input id="suburb" value={values.suburb} disabled={values.suburbUndecided} onChange={e => update("suburb", e.target.value)} autoComplete="address-level2" aria-invalid={!!errors.suburb} aria-describedby={errors.suburb ? "suburb-error" : undefined} className={`${input} ${fieldBorder(errors.suburb)} disabled:bg-paper disabled:text-slate`} data-id="element-479" data-mp="1er0hkpdxbwhi" />
            <label className="mt-3 inline-flex min-h-[44px] cursor-pointer items-center gap-3 text-[15px]" data-id="element-480" data-mp="w1st5rqjf5b8">
              <input type="checkbox" checked={values.suburbUndecided} onChange={e => {
            update("suburbUndecided", e.target.checked);
            update("suburb", "");
          }} className="h-4 w-4 accent-charcoal" data-id="element-481" data-mp="22i4rptx9up7c" />
              Still deciding
            </label>
            {errors.suburb && <p id="suburb-error" className="mt-1 text-sm text-alert" data-id="element-482" data-mp="2hn0k5cn7l9s2">{errors.suburb}</p>}
          </div>

          <div data-id="element-483" data-mp="2cnwakkwdha17">
            <label htmlFor="timing" className={label} data-id="element-484" data-mp="27v35tx0wm1p4">When would you like to start? <span className="font-normal text-slate" data-id="element-485" data-mp="27ta1wphodvkt">(optional)</span></label>
            <select id="timing" value={values.timing} onChange={e => update("timing", e.target.value)} className={`${input} ${fieldBorder()}`} data-id="element-486" data-mp="23sq3lild8lx3">
              <option value="" data-id="element-487" data-mp="1tvbnxl79o15u">Select an option</option>
              {timingOptions.map(option => <option key={option} data-id="element-488" data-mp="1txoeunv05lv1">{option}</option>)}
            </select>
          </div>

          <button type="submit" className={buttonClass("primary", "w-full sm:w-auto")} data-id="element-489" data-mp="1oybgb5urqqcm">
            Continue <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-490" data-mp="1kwq8hyx3d29a" />
          </button>
        </form>}

      {step === 2 && <form onSubmit={onSubmit} noValidate className="mt-6 space-y-6" data-id="element-491" data-mp="3f8xyqyygwuyq">
          <h3 ref={headingRef} tabIndex={-1} className="text-xl font-semibold focus:outline-none" data-id="element-492" data-mp="3f6tw0kcb2201">How should we contact you?</h3>
          <div data-id="element-493" data-mp="18hil0a6mh9lc">
            <label htmlFor="name" className={label} data-id="element-494" data-mp="18fxqulo7zso0">Your name</label>
            <input id="name" value={values.name} onChange={e => update("name", e.target.value)} autoComplete="name" aria-invalid={!!errors.name} aria-describedby={errors.name ? "name-error" : undefined} className={`${input} ${fieldBorder(errors.name)}`} data-id="element-495" data-mp="1mgiafk1ylsik" />
            {errors.name && <p id="name-error" className="mt-2 text-sm text-alert" data-id="element-496" data-mp="tq0aut0g299w">{errors.name}</p>}
          </div>

          <fieldset data-id="element-497" data-mp="oqx5t8kgs31x">
            <legend className={label} data-id="element-498" data-mp="13hmh0axxdi5s">Preferred contact method</legend>
            <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-499" data-mp="13jz7xdlnv2uz">
              {(["email", "phone"] as const).map(method => <label key={method} className={`flex min-h-[52px] cursor-pointer items-center gap-3 rounded-xl border px-4 text-[15px] ${values.contactMethod === method ? "border-charcoal bg-paper" : "border-line"}`} data-id="element-500" data-mp="b1lio2ee4p7i">
                  <input type="radio" name="contactMethod" checked={values.contactMethod === method} onChange={() => update("contactMethod", method)} className="h-4 w-4 accent-charcoal" data-id="element-501" data-mp="2hpc3xzicuuih" />
                  {method === "email" ? "Email" : "Phone"}
                </label>)}
            </div>
          </fieldset>

          {values.contactMethod === "email" ? <div data-id="element-502" data-mp="30gbyrhcj6yjo">
              <label htmlFor="email" className={label} data-id="element-503" data-mp="30ftv3znjhi1q">Email address</label>
              <input id="email" type="email" value={values.email} onChange={e => update("email", e.target.value)} autoComplete="email" aria-invalid={!!errors.email} aria-describedby={errors.email ? "email-error" : undefined} className={`${input} ${fieldBorder(errors.email)}`} data-id="element-504" data-mp="30einfoqz3q5c" />
              {errors.email && <p id="email-error" className="mt-2 text-sm text-alert" data-id="element-505" data-mp="2wgky8lx7d41y">{errors.email}</p>}
            </div> : <div data-id="element-506" data-mp="1l04gun238dcg">
              <label htmlFor="phone" className={label} data-id="element-507" data-mp="1l0mm296qa1wi">Phone number</label>
              <input id="phone" type="tel" value={values.phone} onChange={e => update("phone", e.target.value)} autoComplete="tel" aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "phone-error" : undefined} className={`${input} ${fieldBorder(errors.phone)}`} data-id="element-508" data-mp="3mohe923ja06p" />
              {errors.phone && <p id="phone-error" className="mt-2 text-sm text-alert" data-id="element-509" data-mp="38xm13dm04wxb">{errors.phone}</p>}
            </div>}

          <div data-id="element-510" data-mp="33yj84rjaqh5p">
            <label htmlFor="message" className={label} data-id="element-511" data-mp="33zaurcux14ju">Anything you’d like us to know? <span className="font-normal text-slate" data-id="element-512" data-mp="2yyn1ekj7sbmg">(optional)</span></label>
            <textarea id="message" rows={4} value={values.message} onChange={e => update("message", e.target.value)} className={`${input} ${fieldBorder()}`} data-id="element-513" data-mp="2u03ful8j91um" />
          </div>

          <div data-id="element-514" data-mp="2q0kvbqkmissb">
            <label htmlFor="budget" className={label} data-id="element-515" data-mp="2q0uiz7i74nuu">Intended build budget <span className="font-normal text-slate" data-id="element-516" data-mp="s86tegktu7l2">(optional, excluding land)</span></label>
            <select id="budget" value={values.budget} onChange={e => update("budget", e.target.value)} className={`${input} ${fieldBorder()}`} data-id="element-517" data-mp="o8g46hay7zin">
              <option value="" data-id="element-518" data-mp="2b3utttd2axs1">Select an option</option>
              {budgetOptions.map(option => <option key={option} data-id="element-519" data-mp="2b1i4gv4z5l4y">{option}</option>)}
            </select>
          </div>

          <label className="flex cursor-pointer items-start gap-3 text-[15px] text-slate" data-id="element-520" data-mp="2fwny6g4mc43l">
            <input type="checkbox" checked={values.marketingConsent} onChange={e => update("marketingConsent", e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-charcoal" data-id="element-521" data-mp="2jvdccaxhdpoa" />
            Optional: send me occasional updates from Oceanside Living. You can unsubscribe at any time.
          </label>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center" data-id="element-522" data-mp="1w8oo77hvp0z8">
            <button type="button" onClick={back} className={buttonClass("secondary")} data-id="element-523" data-mp="216yilfvs3xjs">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-524" data-mp="37l7laheoiu39" /> Back
            </button>
            <button type="submit" className={buttonClass("primary")} data-id="element-525" data-mp="3cjisslhi4sib">Request my consultation</button>
          </div>
        </form>}
    </div>;
}